/* eslint-disable unicorn/no-thenable */

type Setter<in S, out T, in B> = (newValue: B, prevSource: S) => T;

type Getter<in S, out A> = (source: S) => A;

interface LensLike<in S, out T, out A, in B> {
	get: Getter<S, A>;
	set: Setter<S, T, B>;
}

/**
 * A pair of functions that get and set a value to/from another value.
 *
 * Warning: These lenses do not always follow the lens laws! The name "lens"
 * comes from a similar concept in functional-programming, however, this lens
 * does not always follow the rules of true functional-programming lenses.
 *
 * -----
 *
 * A lens `l: Lens<S, T, A, B>` is pair of get and set functions that can be
 * used to convert functions from `A` to `B` into functions from `S` to `T`. The
 * dotted arrow below is the `f` argument of `l.modify(f, s)`.
 *
 * ```txt
 *  S >------set--> T
 *  |         |
 * get        |
 *  |         |
 *  v         ^
 *  A ······> B
 * ```
 *
 * -----
 *
 *  Lenses `x: Lens<S, T, A, B>` and `y: Lens<A, B, U, V>` can be composed
 * `x.then(y)`.
 *
 * ```txt
 *  S >------------x.set--> T
 *  |                |
 * x.get             |
 *  |                |
 *  v                ^
 *  A >-----y.set--> B
 *  |         |
 * y.get      |
 *  |         |
 *  v         ^
 *  U ······> V
 * ```
 */
export class Lens<in S, out T, out A, in B> implements LensLike<S, T, A, B> {
	constructor(
		public readonly get: Getter<S, A>,
		public readonly set: Setter<S, T, B>
	) {}

	/**
	 * Compose this lens with another lens.
	 */
	then<U, V>(other: LensLike<A, B, U, V>): Lens<S, T, U, V>;

	/**
	 * Compose this lens with a getter function.
	 */
	then<U>(other: { get: Getter<A, U>; set?: never }): Lens<S, T, U, B>;

	/**
	 * Compose this lens with a setter function.
	 */
	then<V>(other: { get?: never; set: Setter<A, B, V> }): Lens<S, T, A, V>;

	/**
	 * Compose this lens with nothing. (This is essentially a no-op.)
	 */
	then(other?: { get?: never; set?: never }): Lens<S, T, A, B>;

	/**
	 * Compose this lens with a getter, setter, both, or neither.
	 *
	 * This overload is to prevent type errors in when this is used polymorphically.
	 */
	then<U, V>(other?: Partial<LensLike<A, B, U, V>>): Lens<S, T, A | U, B & V>;

	then<U, V>({ get, set }: Partial<LensLike<A, B, U, V>> = {}) {
		return new Lens<S, T, A | U, B & V>(
			get ? (s: S): U => get(this.get(s)) : this.get,
			set ? (a: V, s: S): T => this.set(set(a, this.get(s)), s) : this.set
		);
	}

	modify(f: (a: A) => B, s: S): T {
		return this.set(f(this.get(s)), s);
	}
}
